import { useEffect, type CSSProperties } from 'react';

/* 2026-09-26 (design wave) · the in-app loader half of the load splash.
   index.html paints #rv-splash (a CSS-3D radar) before any JS runs; the
   session check (Splash.tsx) and the bundle fetch (WedgeRoute BrandLoader)
   render this SAME markup with the same .rv-splash classes, which live in
   index.html's inline <style> so they exist before the stylesheet chunk. When
   the HTML splash fades on the first React commit, the frame underneath is
   pixel-for-pixel the same mark, so the rep sees one continuous load screen
   instead of a radar that swaps to a different loader. */

const BLIPS: Array<{ x: string; y: string; d: string; amber?: boolean }> = [
  { x: '73%', y: '24%', d: '0.28s', amber: true },
  { x: '80%', y: '62%', d: '0.75s' },
  { x: '34%', y: '78%', d: '1.40s', amber: true },
  { x: '22%', y: '34%', d: '2.0s' },
];

function splashPhase(): string {
  try {
    const t0 = (window as unknown as { __RV_SPLASH_T0__?: number }).__RV_SPLASH_T0__;
    if (typeof t0 === 'number' && t0 > 0) return `${-Math.max(0, Date.now() - t0)}ms`;
  } catch { /* no inline splash · start fresh */ }
  return '0s';
}

export function RadarSplash({ label }: { label: string }) {
  return (
    <div className="rv-splash rv-splash--app" role="status" aria-label={label} style={{ '--rv-sp-phase': splashPhase() } as CSSProperties}>
      <div className="rv-sp-stage" aria-hidden="true">
        <div className="rv-sp-shadow" />
        <div className="rv-sp-disc">
          <div className="rv-sp-face" />
          <div className="rv-sp-sweep" />
          <div className="rv-sp-sweep rv-sp-sweep-b" />
          {BLIPS.map((b) => (
            <i
              key={b.d}
              className={b.amber ? 'rv-sp-blip rv-sp-amber' : 'rv-sp-blip'}
              style={{ '--x': b.x, '--y': b.y, '--d': b.d } as CSSProperties}
            />
          ))}
        </div>
        <div className="rv-sp-core" />
      </div>
      <div className="rv-sp-word" aria-hidden="true">
        <span className="rv-sp-rep">REP</span>
        <span className="rv-sp-van">VANTAGE</span>
      </div>
    </div>
  );
}

/* Fades the index.html splash out. Guarded: the inline script may be absent
   (tests, a stripped host page) and the release is idempotent there. */
export function releaseBootSplash(): void {
  try {
    const release = (window as unknown as { __RV_SPLASH_RELEASE__?: () => void }).__RV_SPLASH_RELEASE__;
    if (typeof release === 'function') release();
  } catch { /* splash is cosmetic · never block boot */ }
}

/* Mounted once at the top of the React tree; its effect runs after the first
   commit is in the DOM, which is exactly "the app has mounted". */
export function BootSplashRelease(): null {
  useEffect(() => { releaseBootSplash(); }, []);
  return null;
}
